/**
 * The ONE reader of a server document's seed blocks (PROTECTOR-SMS-001 closing prompt 1, D3).
 *
 * `buildHtmlDocument` is the ONE writer: each seed is a `<script type="application/json" id="…">` data block under the
 * caller's element id. Every reader (the tenant blog's `readBlogSsrSeed`, and storefront-cms's page, chrome and data
 * seeds) used to repeat the same steps: find the block, parse it, and treat a block that is not JSON as no seed. They
 * read through this module now, and each keeps only the judgement of its own shape.
 *
 * - `readSeedBlock(elementId)` answers the parsed block, or null with no document (a server render), no block, an
 *   empty block, a block that is not JSON (truncated in transit), or one that is not a JSON object (the writer writes
 *   only objects, `assertSeeds`). Any other failure is a defect and is thrown.
 * - `clearSeedBlock(elementId)` removes the block once its reader has used it, so nothing reads it twice.
 *
 * @module @puffin-core/marketing-shell/prerender/seedBlock
 */

/**
 * @param {unknown} elementId
 * @param {string} name - the caller, for the refusal
 * @returns {void}
 * @throws {TypeError} For an element id that is not a non-empty string.
 */
function assertElementId(elementId, name) {
  if (typeof elementId !== 'string' || elementId === '') {
    throw new TypeError(`${name}: a seed block's element id (a non-empty string) is required`);
  }
}

/**
 * @param {string} elementId
 * @returns {?HTMLElement} the block, or null (none, or no document: a server render)
 */
function seedElement(elementId) {
  return typeof document === 'undefined' ? null : document.getElementById(elementId);
}

/**
 * @param {string} elementId - the seed block's element id, as its writer named it
 * @returns {?Object<string, *>} the parsed block, its values as `JSON.parse` answers them (each reader judges its own
 *   shape), or null (no document, no block, an empty block, one that is not JSON, or one that is not a JSON object)
 * @throws {TypeError} For an element id that is not a non-empty string.
 */
export function readSeedBlock(elementId) {
  assertElementId(elementId, 'readSeedBlock');
  const element = seedElement(elementId);
  if (!element || !element.textContent) return null;
  let seed;
  try {
    seed = JSON.parse(element.textContent);
  } catch (error) {
    // A block that is not JSON (truncated in transit) is no seed: its reader then reads as it would anywhere. Anything
    // else is a defect.
    if (!(error instanceof SyntaxError)) throw error;
    return null;
  }
  return seed !== null && typeof seed === 'object' && !Array.isArray(seed) ? seed : null;
}

/**
 * Free a one-shot seed block once its reader has used it. Call from a post-commit effect, never during render.
 *
 * @param {string} elementId - the seed block's element id
 * @returns {void}
 * @throws {TypeError} For an element id that is not a non-empty string.
 */
export function clearSeedBlock(elementId) {
  assertElementId(elementId, 'clearSeedBlock');
  const element = seedElement(elementId);
  if (element) element.remove();
}
